import { db } from '@/firebase-config';
import { doc, collection, onSnapshot, serverTimestamp, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { logger } from '@/utils/logger';
import { buildProjectSubcollectionPath } from './project-scope';

const PRESENCE_TIMEOUT_MS = 60_000; // 1 min timeout (generous for network blips)
const HEARTBEAT_INTERVAL_MS = 10_000; // 10s heartbeat

export interface UserPresence {
    uid: string;
    displayName: string;
    photoURL?: string;
    lastSeen: Timestamp; 
    activeTab: string;
}

/**
 * Hook to manage user presence and detect other users.
 * - Sends heartbeat with current activeTab.
 * - Listens to other users in the project.
 * - Returns list of active users & counts (for smart sync).
 */
export function usePresence() {
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const isCloudEnabled = useAppStore(state => state.cloud.enabled);
    const activeTab = useAppStore(state => state.ui.activeTab);
    const user = useAppStore(state => state.auth.user);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);
    const presenceUserId = user?.authUid || user?.uid || '';
    
    const [activeUsers, setActiveUsers] = useState<UserPresence[]>([]);

    // Track activeTab in a ref so the heartbeat interval always reads the 
    // latest value without restarting the effect (which causes presence flicker)
    const activeTabRef = useRef(activeTab);
    useEffect(() => {
        activeTabRef.current = activeTab;
    }, [activeTab]);
    
    // 1. Heartbeat Effect — stable interval, does NOT depend on activeTab
    useEffect(() => {
        if (!orgId || !projectId || !isCloudEnabled || !user || !presenceUserId || !isAuthInitialized) return;

        const presenceRef = doc(db, buildProjectSubcollectionPath({ orgId, projectId }, 'presence'), presenceUserId);

        const sendHeartbeat = async () => {
            try {
                await setDoc(presenceRef, {
                    uid: presenceUserId,
                    displayName: user.displayName || 'Anonymous',
                    photoURL: user.photoURL || null,
                    lastSeen: serverTimestamp(),
                    activeTab: activeTabRef.current || 'dashboard'
                }, { merge: true });
            } catch (error: unknown) {
                logger.error("[Presence] Heartbeat failed", error);
            }
        };

        void sendHeartbeat(); // Initial
        const interval = setInterval(() => { void sendHeartbeat(); }, HEARTBEAT_INTERVAL_MS);

        const cleanup = () => {
             deleteDoc(presenceRef).catch((error: unknown) => logger.warn("Cleanup failed", error));
        };

        return () => {
            clearInterval(interval);
            cleanup();
        };

    }, [orgId, projectId, isCloudEnabled, user, presenceUserId, isAuthInitialized]);

    // 2. Listen to Project Presence
    useEffect(() => {
        if (!orgId || !projectId || !isCloudEnabled || !user || !isAuthInitialized) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setActiveUsers([]);
            return;
        }

        const presenceCollection = collection(db, buildProjectSubcollectionPath({ orgId, projectId }, 'presence'));
        
        const unsub = onSnapshot(presenceCollection, (snapshot) => {
            const now = Date.now();
            const users: UserPresence[] = [];
            
            for (const doc of snapshot.docs) {
                const data = doc.data() as UserPresence;
                // Client-side filtering for online status
                if (data.lastSeen) {
                    const secs = data.lastSeen.seconds ? data.lastSeen.seconds : 0;
                    const diff = now - (secs * 1000);
                    if (diff < PRESENCE_TIMEOUT_MS) {
                        users.push(data);
                    }
                }
            }
            
            setActiveUsers(users);
        }, (error: unknown) => {
            logger.error("[usePresence] Listener error:", error);
        });

        return () => unsub();
    }, [orgId, projectId, isCloudEnabled, user, isAuthInitialized]); // Added user dependency

    return { activeUsers };
}













